import { useEffect, useCallback, useRef } from 'react';

const SDK_URL = 'https://static.pipl.com/sdk/device-intelligence/latest/device-intelligence.min.js';

let loadPromise = null;
let isInitialized = false;

function loadScript(src) {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    if (window.DeviceIntelligence) {
      resolve();
    } else {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function onLoad() {
        resolve();
      };
      script.onerror = function onError() {
        reject(new Error(`Failed to load script: ${src}`));
      };
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

function getErrorMessage(error) {
  if (error && error.message) {
    return error.message;
  }
  return error;
}

export function useDeviceIntelligenceNewSdk(apiKey, apiUrl, consentId) {
  const readyPromiseRef = useRef(null);

  useEffect(() => {
    if (!apiKey) {
      return undefined;
    }

    readyPromiseRef.current = loadScript(SDK_URL)
      .then(() => {
        if (!isInitialized && window.DeviceIntelligence) {
          const initOptions = { apiKey, endpoint: apiUrl, behavioral: true };
          if (consentId !== null && consentId !== undefined) {
            initOptions.consentId = consentId;
          }
          window.DeviceIntelligence.init(initOptions);
          isInitialized = true;
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Device Intelligence SDK loading failed:', getErrorMessage(error));
        }
      });

    return undefined;
  }, [apiKey, apiUrl, consentId]);

  const addUserData = useCallback((userData) => {
    const promise = readyPromiseRef.current;
    if (!promise) {
      if (!window.DeviceIntelligence) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('DeviceIntelligence SDK not loaded');
        }
        return Promise.resolve();
      }
      return window.DeviceIntelligence.addUserData(userData);
    }

    return promise.then(() => {
      if (!window.DeviceIntelligence) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('DeviceIntelligence SDK not loaded');
        }
        return undefined;
      }
      return window.DeviceIntelligence.addUserData(userData);
    });
  }, []);

  const getElephantDevice = useCallback(() => {
    if (window.DeviceIntelligence && window.DeviceIntelligence.getElephantDevice) {
      return window.DeviceIntelligence.getElephantDevice();
    }
    return null;
  }, []);

  return { addUserData, getElephantDevice };
}

/**
 * Reads the API key from window.__CONFIG__, initializes the SDK, and sends user data when available.
 */
export function useDeviceIntelligenceNewSdkWithUser(user) {
  const apiKey = window.__CONFIG__ && window.__CONFIG__.DEVICE_INTELLIGENCE_NEW_SDK_API_KEY;
  const apiUrl = window.__CONFIG__ && window.__CONFIG__.DEVICE_INTELLIGENCE_NEW_SDK_API_URL;

  const result = useDeviceIntelligenceNewSdk(apiKey, apiUrl);
  const addUserData = result.addUserData;
  const getElephantDevice = result.getElephantDevice;

  useEffect(() => {
    if (!apiKey || !user) {
      return undefined;
    }

    addUserData({
      user_id: user.id,
      user_email: user.email,
      user_full_name: user.fullName,
    }).catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to send user data to Device Intelligence SDK:', getErrorMessage(error));
      }
    });

    return undefined;
  }, [user, apiKey, addUserData]);

  return { addUserData, getElephantDevice };
}

export default useDeviceIntelligenceNewSdk;
